import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWaveSquare, 
  faPlay,
  faPause,
  faDownload, 
  faMicrophone, 
  faGuitar, 
  faDrum, 
  faKeyboard, 
  faVolumeHigh,
  faVolumeUp,
  faVolumeMute,
  faVolumeDown,
  faSliders,
  faExclamationTriangle,
  faSpinner,
  faRedo
} from '@fortawesome/free-solid-svg-icons';

const StemsVisualization = ({ project }) => {
  const [stemData, setStemData] = useState([]);
  const [audioStates, setAudioStates] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const audioRefs = useRef({});
  const canvasRefs = useRef({});
  const audioContexts = useRef({});
  const analyzers = useRef({});
  const animationRefs = useRef({});

  // Extract stem data from parent stem asset metadata
  useEffect(() => {
    console.log('[StemsVisualization] Project data:', project);
    
    if (project?.audio_assets) {
      const parentStemAsset = project.audio_assets.find(asset => 
        asset.type === 'STEM' && 
        asset.status === 'READY' &&
        asset.metadata?.result && 
        Object.keys(asset.metadata.result).length > 0
      );
      
      console.log('[StemsVisualization] Found parent stem asset:', parentStemAsset);
      
      if (parentStemAsset && parentStemAsset.metadata.result) {
        const stemOrder = ['vocals', 'bass', 'drums', 'keys', 'other', 'guitars'];
        const stems = [];
        
        stemOrder.forEach(stemType => {
          if (parentStemAsset.metadata.result[stemType]) {
            console.log(`[StemsVisualization] Found ${stemType} stem URL:`, parentStemAsset.metadata.result[stemType]);
            stems.push({
              id: `${parentStemAsset.id}_${stemType}`,
              type: stemType,
              url: parentStemAsset.metadata.result[stemType],
              fileName: `${stemType}_${parentStemAsset.file_name}`,
              parentAsset: parentStemAsset
            });
          }
        });
        
        console.log('[StemsVisualization] Total stems found:', stems.length, stems);
        setStemData(stems);
        
        // Initialize audio states
        const initialStates = {};
        const initialLoadingStates = {};
        const initialErrorStates = {};
        stems.forEach(stem => {
          initialStates[stem.id] = {
            isPlaying: false,
            isMuted: false,
            volume: 0.7,
            currentTime: 0,
            duration: 0,
            lowGain: 1,
            highGain: 1
          };
          initialLoadingStates[stem.id] = false;
          initialErrorStates[stem.id] = null;
        });
        setAudioStates(initialStates);
        setLoadingStates(initialLoadingStates);
        setErrorStates(initialErrorStates);
      } else {
        setStemData([]);
      }
    }
  }, [project]);

  // Initialize audio context and analyzer for visualization
  const initializeAudio = (stemId) => {
    const audio = audioRefs.current[stemId];
    if (!audio || audioContexts.current[stemId]) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaElementSource(audio);
      const analyzer = audioContext.createAnalyser();
      
      // EQ filters
      const lowFilter = audioContext.createBiquadFilter();
      lowFilter.type = 'lowshelf';
      lowFilter.frequency.setValueAtTime(320, audioContext.currentTime);
      
      const highFilter = audioContext.createBiquadFilter();
      highFilter.type = 'highshelf';
      highFilter.frequency.setValueAtTime(3200, audioContext.currentTime);

      // Connect audio graph
      source.connect(lowFilter);
      lowFilter.connect(highFilter);
      highFilter.connect(analyzer);
      analyzer.connect(audioContext.destination);

      analyzer.fftSize = 256;
      
      audioContexts.current[stemId] = {
        context: audioContext,
        source,
        analyzer,
        lowFilter,
        highFilter
      };
      analyzers.current[stemId] = analyzer;
      
      startVisualization(stemId);
    } catch (error) {
      console.warn('Could not initialize audio context:', error);
    }
  };

  // Start audio visualization
  const startVisualization = (stemId) => {
    const canvas = canvasRefs.current[stemId];
    const analyzer = analyzers.current[stemId];
    if (!canvas || !analyzer) return;

    const ctx = canvas.getContext('2d');
    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyzer.getByteFrequencyData(dataArray);
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = canvas.width / bufferLength * 2.5;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        
        // Purple gradient
        const gradient = ctx.createLinearGradient(0, canvas.height, 0, canvas.height - barHeight);
        gradient.addColorStop(0, 'rgba(147, 51, 234, 0.8)');
        gradient.addColorStop(0.5, 'rgba(168, 85, 247, 0.6)');
        gradient.addColorStop(1, 'rgba(196, 125, 255, 0.4)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
      
      animationRefs.current[stemId] = requestAnimationFrame(draw);
    };
    
    draw();
  };

  // Get icon for stem type
  const getStemIcon = (stemType) => {
    const iconMap = {
      'vocals': faMicrophone,
      'bass': faGuitar,
      'drums': faDrum,
      'keys': faKeyboard,
      'other': faVolumeHigh
    };
    return iconMap[stemType] || faWaveSquare;
  };

  // Get color for stem type
  const getStemColor = (stemType) => {
    const colorMap = {
      'vocals': 'rgba(147, 51, 234, 0.8)',
      'bass': 'rgba(126, 34, 206, 0.8)',
      'drums': 'rgba(168, 85, 247, 0.8)',
      'keys': 'rgba(196, 125, 255, 0.8)',
      'other': 'rgba(139, 69, 219, 0.8)'
    };
    return colorMap[stemType] || 'rgba(147, 51, 234, 0.8)';
  };

  // Audio event handlers
  const handleAudioEvents = (stemId) => ({
    onPlay: () => {
      console.log(`[StemsVisualization] Audio playing: ${stemId}`);
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], isPlaying: true }
      }));
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
      setErrorStates(prev => ({ ...prev, [stemId]: null }));
    },
    onPause: () => {
      console.log(`[StemsVisualization] Audio paused: ${stemId}`);
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], isPlaying: false }
      }));
    },
    onTimeUpdate: (e) => setAudioStates(prev => ({
      ...prev,
      [stemId]: { ...prev[stemId], currentTime: e.target.currentTime }
    })),
    onLoadedMetadata: (e) => {
      console.log(`[StemsVisualization] Audio metadata loaded: ${stemId}`, {
        duration: e.target.duration,
        readyState: e.target.readyState
      });
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], duration: e.target.duration }
      }));
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
      setErrorStates(prev => ({ ...prev, [stemId]: null }));
    },
    onLoadedData: (e) => {
      console.log(`[StemsVisualization] Audio data loaded: ${stemId}`);
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
    },
    onCanPlay: (e) => {
      console.log(`[StemsVisualization] Audio can play: ${stemId}`);
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
      setErrorStates(prev => ({ ...prev, [stemId]: null }));
    },
    onLoadStart: (e) => {
      console.log(`[StemsVisualization] Audio load started: ${stemId}`);
      setLoadingStates(prev => ({ ...prev, [stemId]: true }));
      setErrorStates(prev => ({ ...prev, [stemId]: null }));
    },
    onError: (e) => {
      console.error(`[StemsVisualization] Audio error for ${stemId}:`, {
        error: e.target.error,
        src: e.target.src,
        readyState: e.target.readyState,
        networkState: e.target.networkState
      });
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
      setErrorStates(prev => ({ 
        ...prev, 
        [stemId]: `Failed to load audio: ${e.target.error?.message || 'Unknown error'}` 
      }));
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], isPlaying: false }
      }));
    },
    onEnded: () => setAudioStates(prev => ({
      ...prev,
      [stemId]: { ...prev[stemId], isPlaying: false }
    }))
  });

  // Handle play/pause with better error handling
  const togglePlay = async (stemId) => {
    const audio = audioRefs.current[stemId];
    const state = audioStates[stemId];
    
    if (!audio) {
      console.error(`[StemsVisualization] Audio element not found for ${stemId}`);
      return;
    }

    console.log(`[StemsVisualization] Toggle play for ${stemId}:`, {
      currentlyPlaying: state.isPlaying,
      readyState: audio.readyState,
      networkState: audio.networkState,
      src: audio.src
    });

    try {
      if (state.isPlaying) {
        audio.pause();
      } else {
        setLoadingStates(prev => ({ ...prev, [stemId]: true }));
        setErrorStates(prev => ({ ...prev, [stemId]: null }));
        
        if (!audioContexts.current[stemId]) {
          initializeAudio(stemId);
        }
        
        // Test if audio can be loaded first
        if (audio.readyState === 0) {
          console.log(`[StemsVisualization] Audio not ready, loading metadata first for ${stemId}`);
          audio.load();
          
          // Wait for metadata to load or error
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Audio loading timeout'));
            }, 10000); // 10 second timeout
            
            const onCanPlay = () => {
              clearTimeout(timeout);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (e) => {
              clearTimeout(timeout);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              reject(e.target.error || new Error('Audio loading failed'));
            };
            
            audio.addEventListener('canplay', onCanPlay);
            audio.addEventListener('error', onError);
          });
        }
        
        await audio.play();
      }
    } catch (error) {
      console.error(`[StemsVisualization] Audio play error for ${stemId}:`, error);
      setLoadingStates(prev => ({ ...prev, [stemId]: false }));
      setErrorStates(prev => ({ 
        ...prev, 
        [stemId]: `Playback failed: ${error.message}` 
      }));
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], isPlaying: false }
      }));
    }
  };

  // Handle mute toggle
  const toggleMute = (stemId) => {
    const audio = audioRefs.current[stemId];
    const newState = !audioStates[stemId].isMuted;
    
    if (audio) {
      audio.muted = newState;
    }
    
    setAudioStates(prev => ({
      ...prev,
      [stemId]: { ...prev[stemId], isMuted: newState }
    }));
  };

  // Handle volume change
  const handleVolumeChange = (stemId, volume) => {
    const audio = audioRefs.current[stemId];
    
    if (audio) {
      audio.volume = volume;
    }
    
    setAudioStates(prev => ({
      ...prev,
      [stemId]: { ...prev[stemId], volume }
    }));
  };

  // Handle EQ changes
  const handleEQChange = (stemId, type, value) => {
    const audioContext = audioContexts.current[stemId];
    if (!audioContext) return;

    const gain = Math.pow(10, (value - 1) * 2); // Convert to gain value
    
    if (type === 'low') {
      audioContext.lowFilter.gain.setValueAtTime(gain, audioContext.context.currentTime);
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], lowGain: value }
      }));
    } else if (type === 'high') {
      audioContext.highFilter.gain.setValueAtTime(gain, audioContext.context.currentTime);
      setAudioStates(prev => ({
        ...prev,
        [stemId]: { ...prev[stemId], highGain: value }
      }));
    }
  };

  // Handle seek
  const handleSeek = (stemId, seekTime) => {
    const audio = audioRefs.current[stemId];
    if (audio && !isNaN(seekTime) && seekTime >= 0 && seekTime <= audio.duration) {
      audio.currentTime = seekTime;
    }
  };

  // Global controls
  const playAllStems = () => {
    stemData.forEach(stem => {
      if (!audioStates[stem.id]?.isPlaying && !errorStates[stem.id]) {
        togglePlay(stem.id);
      }
    });
  };

  const pauseAllStems = () => {
    stemData.forEach(stem => {
      if (audioStates[stem.id]?.isPlaying) {
        togglePlay(stem.id);
      }
    });
  };

  const restartAllStems = () => {
    stemData.forEach(stem => {
      const audio = audioRefs.current[stem.id];
      if (audio) {
        audio.currentTime = 0;
        if (audioStates[stem.id]?.isPlaying) {
          audio.play();
        }
      }
    });
  };

  // Handle download
  const handleDownloadStem = (stem) => {
    const link = document.createElement('a');
    link.href = stem.url;
    link.download = stem.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(animationRefs.current).forEach(id => {
        if (id) cancelAnimationFrame(id);
      });
      Object.values(audioContexts.current).forEach(({ context }) => {
        if (context && context.state !== 'closed') {
          context.close();
        }
      });
    };
  }, []);

  if (stemData.length === 0) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(147, 51, 234, 0.2)',
        borderRadius: '12px',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <small style={{ color: 'rgba(147, 51, 234, 0.6)' }}>
          Generated stems will appear here          
        </small>
      </div>
    );
  }

  return (
    <div className="stems-visualization" style={{ padding: '0.5rem 0' }}>
      {/* Global Controls */}
      <div className="d-flex align-items-center justify-content-center gap-2 mb-3" style={{
        background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(147, 51, 234, 0.2)',
        borderRadius: '8px',
        padding: '0.5rem'
      }}>
        <small style={{ color: 'rgba(147, 51, 234, 0.8)', marginRight: '0.5rem', fontSize: '0.75rem' }}>
          Global:
        </small>
        <Button
          variant="primary"
          size="sm"
          onClick={playAllStems}
          style={{
            background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.8) 0%, rgba(168, 85, 247, 0.8) 100%)',
            border: 'none',
            padding: '0.25rem 0.5rem',
            fontSize: '0.7rem'
          }}
        >
          <FontAwesomeIcon icon={faPlay} className="me-1" size="xs" />
          Play All
        </Button>
        <Button
          variant="warning"
          size="sm"
          onClick={pauseAllStems}
          style={{
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.8) 0%, rgba(245, 158, 11, 0.8) 100%)',
            border: 'none',
            padding: '0.25rem 0.5rem',
            fontSize: '0.7rem'
          }}
        >
          <FontAwesomeIcon icon={faPause} className="me-1" size="xs" />
          Pause All
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={restartAllStems}
          style={{
            background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.8) 0%, rgba(75, 85, 99, 0.8) 100%)',
            border: 'none',
            padding: '0.25rem 0.5rem',
            fontSize: '0.7rem'
          }}
        >
          <FontAwesomeIcon icon={faRedo} className="me-1" size="xs" />
          Restart All
        </Button>
      </div>

      <Row className="g-3">
        {stemData.map((stem, index) => {
          const state = audioStates[stem.id] || {};
          
          return (
            <Col key={stem.id} lg={6} md={6} sm={12}>
              <div 
                style={{
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(168, 85, 247, 0.04) 100%)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(147, 51, 234, 0.15)',
                  borderRadius: '12px',
                  padding: '0.75rem',
                  height: '100%',
                  boxShadow: '0 4px 16px rgba(147, 51, 234, 0.08)'
                }}
              >
                {/* Hidden audio element */}
                <audio
                  ref={el => audioRefs.current[stem.id] = el}
                  src={stem.url}
                  preload="metadata"
                  crossOrigin="anonymous"
                  {...handleAudioEvents(stem.id)}
                />
                
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <div 
                      style={{ 
                        background: getStemColor(stem.type),
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        borderRadius: '8px',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '0.75rem',
                        boxShadow: '0 2px 8px rgba(147, 51, 234, 0.2)'
                      }}
                    >
                      <FontAwesomeIcon icon={getStemIcon(stem.type)} size="sm" />
                    </div>
                    <div>
                      <h6 className="mb-0 text-capitalize" style={{ color: 'rgba(147, 51, 234, 0.9)', fontSize: '0.9rem' }}>
                        {stem.type}
                        {errorStates[stem.id] && (
                          <FontAwesomeIcon 
                            icon={faExclamationTriangle} 
                            className="ms-2" 
                            style={{ color: '#dc3545', fontSize: '0.75rem' }}
                            title={errorStates[stem.id]}
                          />
                        )}
                        {loadingStates[stem.id] && (
                          <FontAwesomeIcon 
                            icon={faSpinner} 
                            spin 
                            className="ms-2" 
                            style={{ color: 'rgba(147, 51, 234, 0.7)', fontSize: '0.75rem' }}
                          />
                        )}
                      </h6>
                      <small style={{ color: 'rgba(147, 51, 234, 0.6)', fontSize: '0.75rem' }}>
                        {formatTime(state.currentTime || 0)} / {formatTime(state.duration || 0)}
                      </small>
                      {errorStates[stem.id] && (
                        <div style={{ color: '#dc3545', fontSize: '0.65rem', marginTop: '0.25rem' }}>
                          {errorStates[stem.id]}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => handleDownloadStem(stem)}
                    disabled={!!errorStates[stem.id]}
                    style={{
                      border: '1px solid rgba(147, 51, 234, 0.3)',
                      color: 'rgba(147, 51, 234, 0.8)',
                      backdropFilter: 'blur(10px)',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      opacity: errorStates[stem.id] ? 0.5 : 1
                    }}
                  >
                    <FontAwesomeIcon icon={faDownload} size="xs" />
                  </Button>
                </div>

                {/* Error Alert */}
                {errorStates[stem.id] && (
                  <Alert variant="danger" className="mb-2" style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.5rem',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    border: '1px solid rgba(220, 53, 69, 0.3)',
                    color: '#dc3545'
                  }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {errorStates[stem.id]}
                  </Alert>
                )}

                {/* Visualization Canvas */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <canvas
                    ref={el => canvasRefs.current[stem.id] = el}
                    width={400}
                    height={60}
                    style={{
                      width: '100%',
                      height: '60px',
                      background: 'rgba(147, 51, 234, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(147, 51, 234, 0.1)',
                      opacity: errorStates[stem.id] ? 0.3 : 1
                    }}
                  />
                </div>

                {/* Compact Inline Controls */}
                <div className="d-flex align-items-center gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
                  {/* Play/Pause - Small Knob Style */}
                  <div
                    onClick={() => togglePlay(stem.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: errorStates[stem.id] 
                        ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.8) 0%, rgba(185, 28, 28, 0.8) 100%)'
                        : state.isPlaying 
                          ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.8) 0%, rgba(245, 158, 11, 0.8) 100%)'
                          : 'linear-gradient(135deg, rgba(147, 51, 234, 0.8) 0%, rgba(168, 85, 247, 0.8) 100%)',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: loadingStates[stem.id] ? 'default' : 'pointer',
                      opacity: loadingStates[stem.id] ? 0.7 : 1,
                      color: 'white',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease'
                    }}
                    title={loadingStates[stem.id] ? 'Loading...' : errorStates[stem.id] ? 'Error' : (state.isPlaying ? 'Pause' : 'Play')}
                  >
                    <FontAwesomeIcon 
                      icon={loadingStates[stem.id] ? faSpinner : errorStates[stem.id] ? faExclamationTriangle : (state.isPlaying ? faPause : faPlay)} 
                      size="xs" 
                      spin={loadingStates[stem.id]}
                    />
                  </div>

                  {/* Mute - Small Knob Style */}
                  <div
                    onClick={() => toggleMute(stem.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: state.isMuted 
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)'
                        : 'linear-gradient(135deg, rgba(107, 114, 128, 0.2) 0%, rgba(75, 85, 99, 0.2) 100%)',
                      border: '1px solid rgba(147, 51, 234, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: (loadingStates[stem.id] || errorStates[stem.id]) ? 'default' : 'pointer',
                      opacity: (loadingStates[stem.id] || errorStates[stem.id]) ? 0.5 : 1,
                      color: state.isMuted ? 'white' : 'rgba(147, 51, 234, 0.8)',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    title={state.isMuted ? 'Unmute' : 'Mute'}
                  >
                    <FontAwesomeIcon icon={state.isMuted ? faVolumeMute : faVolumeUp} size="xs" />
                  </div>

                  {/* Volume Knob */}
                  <div className="d-flex align-items-center" style={{ minWidth: '60px' }}>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={state.volume || 0.7}
                      onChange={(e) => handleVolumeChange(stem.id, parseFloat(e.target.value))}
                      style={{
                        width: '50px',
                        height: '4px',
                        accentColor: 'rgba(147, 51, 234, 0.8)',
                        background: 'rgba(147, 51, 234, 0.2)',
                        borderRadius: '2px'
                      }}
                      disabled={loadingStates[stem.id] || !!errorStates[stem.id]}
                      title={`Volume: ${Math.round((state.volume || 0.7) * 100)}%`}
                    />
                  </div>

                  {/* Low EQ Knob */}
                  <div className="d-flex align-items-center" style={{ minWidth: '45px' }}>
                    <small style={{ color: 'rgba(147, 51, 234, 0.6)', fontSize: '0.65rem', marginRight: '0.25rem' }}>L</small>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={state.lowGain || 1}
                      onChange={(e) => handleEQChange(stem.id, 'low', parseFloat(e.target.value))}
                      style={{
                        width: '35px',
                        height: '4px',
                        accentColor: 'rgba(147, 51, 234, 0.8)',
                        background: 'rgba(147, 51, 234, 0.2)',
                        borderRadius: '2px'
                      }}
                      disabled={loadingStates[stem.id] || !!errorStates[stem.id]}
                      title={`Low: ${Math.round((state.lowGain || 1) * 100)}%`}
                    />
                  </div>

                  {/* High EQ Knob */}
                  <div className="d-flex align-items-center" style={{ minWidth: '45px' }}>
                    <small style={{ color: 'rgba(147, 51, 234, 0.6)', fontSize: '0.65rem', marginRight: '0.25rem' }}>H</small>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={state.highGain || 1}
                      onChange={(e) => handleEQChange(stem.id, 'high', parseFloat(e.target.value))}
                      style={{
                        width: '35px',
                        height: '4px',
                        accentColor: 'rgba(147, 51, 234, 0.8)',
                        background: 'rgba(147, 51, 234, 0.2)',
                        borderRadius: '2px'
                      }}
                      disabled={loadingStates[stem.id] || !!errorStates[stem.id]}
                      title={`High: ${Math.round((state.highGain || 1) * 100)}%`}
                    />
                  </div>
                </div>

                {/* Seek Control - Clickable Progress Bar */}
                <div style={{ marginTop: '0.5rem' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      background: 'rgba(147, 51, 234, 0.2)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const percentage = clickX / rect.width;
                      const seekTime = percentage * (state.duration || 0);
                      handleSeek(stem.id, seekTime);
                    }}
                    title={`Seek to position`}
                  >
                    {/* Progress indicator */}
                    <div
                      style={{
                        width: `${((state.currentTime || 0) / (state.duration || 1)) * 100}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, rgba(147, 51, 234, 0.8) 0%, rgba(168, 85, 247, 0.8) 100%)',
                        transition: 'width 0.1s ease'
                      }}
                    />
                    {/* Hover indicator */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'transparent'
                      }}
                      onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const hoverX = e.clientX - rect.left;
                        const percentage = hoverX / rect.width;
                        const hoverTime = percentage * (state.duration || 0);
                        e.currentTarget.title = `Seek to ${formatTime(hoverTime)}`;
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default StemsVisualization; 